import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { RealtyFilter } from './realty-filter';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useRealtyStore } from '../../../stores/realty-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { clearRealtyFilter } from '../../../utils/realty-filter-contract';
import styles from '../realty-listing.module.css';
import { RealtyFilterSortMobile } from './realty-filter-sort-mobile';
import { useRef } from 'react';
import type { UnderConstructionSearchCapability } from './realty-filter-recipe';

const ListingFilterIcon = () => (
  <svg
    aria-hidden="true"
    className={styles.mobileFilterIcon}
    data-eli-part="listing-filter-icon"
    focusable="false"
    viewBox="0 0 24 24"
  >
    <path
      d="M6.65149 15C6.40016 15 6.18991 14.9153 6.02074 14.746C5.85141 14.5768 5.76674 14.3666 5.76674 14.1152V8.327L0.168743 1.2155C-0.0235907 0.959 -0.0515075 0.692333 0.0849925 0.4155C0.221659 0.1385 0.452159 0 0.776493 0H13.757C14.0813 0 14.3118 0.1385 14.4485 0.4155C14.585 0.692333 14.5571 0.959 14.3647 1.2155L8.76674 8.327V14.1152C8.76674 14.3666 8.68208 14.5768 8.51274 14.746C8.34358 14.9153 8.13333 15 7.88199 15H6.65149ZM7.26674 7.8L12.2167 1.5H2.31674L7.26674 7.8Z"
      fill="currentColor"
      transform="translate(4.73325 4.5)"
    />
  </svg>
);

const ListingFilterCloseIcon = () => (
  <svg aria-hidden="true" className={styles.mobileFilterCloseIcon} viewBox="0 0 24 24">
    <path
      d="M6.34425 7.398L1.27125 12.4713C1.13275 12.6096 0.958667 12.6804 0.749 12.6838C0.5395 12.6869 0.36225 12.6161 0.21725 12.4713C0.0724167 12.3263 0 12.1506 0 11.9443C0 11.7379 0.0724167 11.5623 0.21725 11.4173L5.2905 6.34425L0.21725 1.27125C0.0789167 1.13275 0.00808339 0.958667 0.00475006 0.749001C0.00158339 0.539501 0.0724167 0.36225 0.21725 0.21725C0.36225 0.0724167 0.537917 0 0.74425 0C0.950583 0 1.12625 0.0724167 1.27125 0.21725L6.34425 5.2905L11.4173 0.21725C11.5558 0.0789167 11.7298 0.00808339 11.9395 0.00475006C12.149 0.00158339 12.3263 0.0724167 12.4713 0.21725C12.6161 0.36225 12.6885 0.537917 12.6885 0.74425C12.6885 0.950584 12.6161 1.12625 12.4713 1.27125L7.398 6.34425L12.4713 11.4173C12.6096 11.5558 12.6804 11.7298 12.6838 11.9395C12.6869 12.149 12.6161 12.3263 12.4713 12.4713C12.3263 12.6161 12.1506 12.6885 11.9443 12.6885C11.7379 12.6885 11.5623 12.6161 11.4173 12.4713L6.34425 7.398Z"
      fill="currentColor"
      transform="translate(5.65575 5.65575)"
    />
  </svg>
);

type RealtyFilterTopProps = {
  filterEnabled?: boolean;
  underConstructionSearchCapability?: UnderConstructionSearchCapability;
};

export const RealtyFilterTop = ({
  filterEnabled = true,
  underConstructionSearchCapability = 'unavailable',
}: RealtyFilterTopProps) => {
  const { t } = useTranslations();
  const { applyFilter, filter, clearFilters } = useRealtyStore();
  const categorySettings = useGlobalStore(state => state.templateSettings?.realty_category);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const showApply = categorySettings?.filter_button_in_filter?.enabled === true;
  const showReset = categorySettings?.reset_button_in_filter?.enabled === true;

  const resetFilter = () => {
    const clearedFilter = clearRealtyFilter(filter);
    clearFilters();
    applyFilter(clearedFilter);
  };

  return (
    <>
      {filterEnabled && (
        <Sheet>
          <SheetTrigger asChild>
            <Button
              variant="secondary"
              className={styles.mobileFilterTrigger}
              data-eli-part="listing-filter-trigger"
            >
              <ListingFilterIcon />
              {t('text174')}
            </Button>
          </SheetTrigger>
          <SheetContent
            side="full"
            showCloseIcon={false}
            className={styles.mobileFilterSheet}
            data-eli-component="realty-filter-sheet"
            aria-describedby={undefined}
            onOpenAutoFocus={event => {
              event.preventDefault();
              titleRef.current?.focus({ preventScroll: true });
            }}
          >
            <div className={styles.mobileFilterScroll} data-eli-part="realty-filter-scroll">
              <div className={styles.mobileFilterHeader} data-eli-part="realty-filter-header">
                <SheetTitle
                  ref={titleRef}
                  tabIndex={-1}
                  className={styles.mobileFilterTitle}
                  data-eli-part="listing-filter-title"
                >
                  {t('text174')}
                </SheetTitle>
                <SheetClose
                  className={styles.mobileFilterClose}
                  aria-label={t('text1855')}
                  data-eli-part="listing-filter-close"
                >
                  <ListingFilterCloseIcon />
                </SheetClose>
              </div>
              <RealtyFilter
                variant="mobile"
                underConstructionSearchCapability={underConstructionSearchCapability}
              />
              <div aria-hidden="true" className={styles.mobileFilterScrollTail} />
            </div>
            {(showApply || showReset) && (
              <div className={styles.mobileFilterFooter} data-eli-part="realty-filter-footer">
                {showApply && (
                  <SheetClose asChild>
                    <Button
                      className={styles.mobileFilterApply}
                      data-eli-part="realty-filter-apply"
                      onClick={() => applyFilter(filter)}
                    >
                      {t('text174')}
                    </Button>
                  </SheetClose>
                )}
                {showReset && (
                  <SheetClose asChild>
                    <Button
                      type="button"
                      variant="secondary"
                      className={styles.mobileFilterReset}
                      data-eli-part="realty-filter-reset"
                      onClick={resetFilter}
                    >
                      {t('text3384')}
                    </Button>
                  </SheetClose>
                )}
              </div>
            )}
          </SheetContent>
        </Sheet>
      )}

      <RealtyFilterSortMobile />
    </>
  );
};
